import { DEFAULT_PROGRAM } from '../constants/defaultProgram';
import { EXERCISE_DATABASE, EXERCISE_MAP } from '../constants/exerciseDatabase';
import { gwn } from './date';

export function getAggregateMuscleDiagram(exercises) {
  const primary = new Set();
  const secondary = new Set();
  exercises.forEach(ex => {
    const dbEx = EXERCISE_MAP.get(ex.id)
            || EXERCISE_DATABASE.find(e => e.name === ex.name);
    if (!dbEx?.muscles) return;
    (dbEx.muscles.primary || []).forEach(m => primary.add(m));
    (dbEx.muscles.secondary || []).forEach(m => { if (!primary.has(m)) secondary.add(m); });
  });
  return { primary: [...primary], secondary: [...secondary] };
}

export function getDayMeta(dayIdx, dayStructure) {
  if (dayStructure && dayStructure[dayIdx]) {
    const d = dayStructure[dayIdx];
    return {
      ...DEFAULT_PROGRAM[dayIdx],
      name: d.name,
      color: d.color || "#4f8fff",
      isRest: d.isRest,
    };
  }
  return DEFAULT_PROGRAM[dayIdx] || { name: "Rest", color: "#4f8fff", isRest: true, day: "Unknown", short: "?", exercises: [] };
}

export function getTrainingDayIndices(dayStructure) {
  if (!dayStructure) return [1, 2, 4, 5, 6];
  return [0,1,2,3,4,5,6].filter(i => !dayStructure[i]?.isRest);
}

export function getDayExercises(dayIndex, customProgram, dayStructure) {
  // If dayStructure says this is a rest day, return empty
  if (dayStructure && dayStructure[dayIndex]?.isRest) return [];
  // If customProgram has an explicit entry for this day (even empty), use it
  // Only fall back to DEFAULT_PROGRAM if the day was never set at all
  if (customProgram && Object.prototype.hasOwnProperty.call(customProgram, dayIndex)) {
    return customProgram[dayIndex] || [];
  }
  return DEFAULT_PROGRAM[dayIndex]?.exercises || [];
}

export function generateId() {
  return "ex-" + Date.now() + "-" + Math.random().toString(36).substr(2, 6);
}

export function getOverloadSuggestion(exercise, sessions, startingWeights) {
  const { rLo, rHi, inc, assisted, id } = exercise;

  // Pull up to 3 real sessions for this exercise, excluding sessions where it was swapped out
  const history = getExerciseHistory(sessions, id, 3);

  if (history.length === 0) {
    if (startingWeights?.[id]) {
      const sw = startingWeights[id];
      return { weight: sw, action: "hold", msg: `${sw} lbs — first session` };
    }
    return null;
  }

  // Weighted capability score for a session:
  // top reps (50%) + avg reps (30%) + last set reps (20%)
  // This captures ceiling, sustainability, and fatigue resistance in one number
  function capScore(sets) {
    const w = sets.filter(s => !s.warmup);
    if (!w.length) return null;
    const top = Math.max(...w.map(s => s.reps));
    const avg = w.reduce((sum, s) => sum + s.reps, 0) / w.length;
    const last = w[w.length - 1].reps;
    return (top * 0.5) + (avg * 0.3) + (last * 0.2);
  }

  const recent = history[0];
  const working = recent.sets.filter(s => !s.warmup);
  if (!working.length) return null;

  const weight = working[working.length - 1].weight;
  const nextWeight = +(weight + inc).toFixed(1);
  const dropWeight = Math.max(0, +(weight - inc).toFixed(1));
  const recentScore = capScore(recent.sets);
  const prevScore = history.length >= 2 ? capScore(history[1].sets) : null;

  // Flag high variance — big drop-off across sets regardless of averages
  const topReps = Math.max(...working.map(s => s.reps));
  const lastReps = working[working.length - 1].reps;
  const highVariance = !assisted && (topReps - lastReps) >= 4;

  if (highVariance) {
    return { weight, action: "hold", msg: `${weight} lbs — big drop-off across sets, rest longer` };
  }

  if (assisted) {
    if (recentScore >= rHi) return { weight: dropWeight, action: "up", msg: `Drop assist → ${dropWeight} lbs` };
    if (recentScore < rLo) return { weight, action: "hold", msg: `${weight} lbs — build reps first` };
    return { weight, action: "hold", msg: `${weight} lbs — push reps` };
  }

  // Single session — be conservative, single data point isn't conclusive
  if (history.length === 1) {
    if (recentScore >= rHi) return { weight: nextWeight, action: "up", msg: `${nextWeight} lbs — hit the ceiling` };
    if (recentScore < rLo) return { weight, action: "hold", msg: `${weight} lbs — tough session, hold weight` };
    return { weight, action: "hold", msg: `${weight} lbs — push reps` };
  }

  // Multi-session — use trend to confirm signals
  if (recentScore >= rHi) {
    // Strong recent session — check if previous was also solid before incrementing
    if (prevScore !== null && prevScore >= rLo) {
      return { weight: nextWeight, action: "up", msg: `${nextWeight} lbs — two sessions confirm it` };
    }
    return { weight, action: "hold", msg: `${weight} lbs — strong today, hold once more to confirm` };
  }

  if (recentScore < rLo) {
    // Below range — only drop if it's been consistent, not just a bad day
    if (prevScore !== null && prevScore < rLo) {
      return { weight: dropWeight, action: "down", msg: `${dropWeight} lbs — consistently below range` };
    }
    return { weight, action: "hold", msg: `${weight} lbs — tough session, hold weight` };
  }

  return { weight, action: "hold", msg: `${weight} lbs — push reps` };
}

export function isPR(exId, weight, reps, sessions) {
  for (const [, sess] of Object.entries(sessions)) {
    const ex = sess.exercises?.[exId];
    if (!ex) continue;
    for (const s of (ex.sets || [])) {
      if (!s.warmup && s.weight >= weight && s.reps >= reps) return false;
    }
  }
  return weight > 0;
}

export function findLastSession(sessions, dayIndex, beforeDate) {
  const e = Object.entries(sessions)
    .filter(([d, s]) => s.dayIndex === dayIndex && d < beforeDate)
    .sort((a, b) => b[0].localeCompare(a[0]));
  return e[0] ? { date: e[0][0], ...e[0][1] } : null;
}

export function getExerciseHistory(sessions, exId, limit = 8) {
  const r = [];
  const sorted = Object.entries(sessions).sort((a, b) => b[0].localeCompare(a[0]));
  for (const [date, sess] of sorted) {
    const ex = sess.exercises?.[exId];
    // Skip sessions where this exercise was swapped out — data belongs to the replacement
    if (ex?.swapped) continue;
    if (ex?.sets?.length > 0) { r.push({ date, sets: ex.sets }); if (r.length >= limit) break; }
  }
  return r;
}

export function getConsecutiveWeeks(sessions) {
  const dates = Object.keys(sessions).sort().reverse();
  if (!dates.length) return 0;
  let weeks = 1, last = gwn(new Date(dates[0]));
  for (let i = 1; i < dates.length; i++) {
    const w = gwn(new Date(dates[i]));
    if (w === last) continue;
    if (last - w === 1 || (last === 1 && w >= 51)) { weeks++; last = w; } else break;
  }
  return weeks;
}

export function getNextTrainingDay(fromDow, customProgram, dayStructure) {
  for (let i = 1; i <= 7; i++) {
    const idx = (fromDow + i) % 7;
    if (getDayExercises(idx, customProgram, dayStructure).length > 0) {
      const meta = getDayMeta(idx, dayStructure);
      return { ...meta, exercises: getDayExercises(idx, customProgram, dayStructure) };
    }
  }
  return null;
}
